import { CSSObject } from '@emotion/react';
import {
    HoneycombTheme,
    TextColorOption,
    TypographySizeProps,
    toPx,
    useHoneycombTheme,
    HeadingSizeMap,
} from '../../../theming';

type StyleClasses = 'root';

const headingStyles = (heading: TypographySizeProps): CSSObject => {
    return {
        ...heading,
        '&:not(:first-of-type)': {
            marginTop: `${toPx(heading.lineHeight)}px`,
        },
    };
};

const getTextColor = (color: TextColorOption, theme: HoneycombTheme): string => {
    return color !== 'inherit' ? theme.fn.getColor(color) : color;
};

export interface TypographyStylesParams {
    headingColor: TextColorOption;
    anchorColor: TextColorOption;
    headingSizeMap: HeadingSizeMap;
}

export function useStyles({
    headingColor: headingColorProp,
    anchorColor: anchorColorProp,
    headingSizeMap,
}: TypographyStylesParams): {
    styles: { [key in StyleClasses]: CSSObject };
} {
    const { theme } = useHoneycombTheme();
    const { typography, spacingUnit } = theme;
    const { body, heading } = typography.variant;

    const headingColor = getTextColor(headingColorProp, theme);
    const anchorColor = getTextColor(anchorColorProp, theme);

    const root: CSSObject = {
        ...theme.fn.fontStyles('body'),
        lineHeight: typography.baseLineHeight,

        [theme.fn.breakpoints.down('s')]: {
            fontSize: body.s.l.fontSize,
        },
        '& h1, & h2, & h3, & h4, & h5, & h6': {
            ...theme.fn.fontStyles('heading'),
            marginBottom: `${spacingUnit * 3}px`,
            color: headingColor,
        },

        '& h1': {
            ...headingStyles(heading[headingSizeMap.h1].l),
        },

        '& h2': {
            ...headingStyles(heading[headingSizeMap.h2].l),
        },

        '& h3': {
            ...headingStyles(heading[headingSizeMap.h3].l),
        },

        '& h4': {
            ...headingStyles(heading[headingSizeMap.h4].l),
        },

        '& h5': {
            ...headingStyles(heading[headingSizeMap.h5].l),
        },

        '& h6': {
            ...headingStyles(heading[headingSizeMap.h6].l),
        },

        '& img': {
            maxWidth: '100%',
            marginBottom: `${spacingUnit * 3}px`,
        },

        '& p': {
            marginTop: 0,
            '&:not(:last-child)': {
                marginBottom: `${spacingUnit * 5}px`,
            },
        },

        '& hr': {
            marginTop: `${spacingUnit * 5}px`,
            marginBottom: `${spacingUnit * 4}px`,
            borderBottom: 0,
            borderLeft: 0,
            borderRight: 0,
            borderTop: `1px dashed currentColor`,
        },

        '& a': {
            ...theme.fn.focusStyles(),
            color: anchorColor,
            textDecoration: 'underline',
            cursor: 'pointer',
        },

        '& ul, & ol': {
            marginBottom: `${spacingUnit * 4}px`,
            paddingLeft: 38,

            '& li': {
                marginTop: `${spacingUnit * 2}px`,
            },
        },
        '& ul': {
            listStyle: 'disc',
        },
        '& ol': {
            listStyle: 'decimal',
        },
    };

    return {
        styles: {
            root,
        },
    };
}
